import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { z } from 'zod';
import { registerResponsiveContract } from '@norkron/web-conformance';

// src/describe-direction.ts
var LEXICONS = {
  change: { improved: "improved by", regressed: "regressed by", unchanged: "unchanged" },
  count: { improved: "increased by", regressed: "decreased by", unchanged: "unchanged" },
  percent: { improved: "improved by", regressed: "regressed by", unchanged: "unchanged" },
  rate: { improved: "sped up by", regressed: "slowed by", unchanged: "unchanged" },
  duration: { improved: "shortened by", regressed: "lengthened by", unchanged: "unchanged" },
  latency: { improved: "faster by", regressed: "slower by", unchanged: "unchanged" },
  "error-rate": {
    improved: "fewer errors by",
    regressed: "more errors by",
    unchanged: "unchanged"
  }
};
function describeDirection(input) {
  const { value, invert = false, kind = "change" } = input;
  const lex = LEXICONS[kind] ?? LEXICONS.change;
  if (!Number.isFinite(value) || value === 0) {
    return { srText: lex.unchanged, tone: "neutral", isImprovement: false };
  }
  const isImprovement = invert ? value < 0 : value > 0;
  return {
    srText: isImprovement ? lex.improved : lex.regressed,
    tone: isImprovement ? "good" : "bad",
    isImprovement
  };
}
var NUMFMT_LIB = "@norkron/web-numeric-format";
var NUMFMT_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.numeric-format");
var { warn, panic } = createScopedErrors(NUMFMT_LIB, NUMFMT_TOKEN);
try {
  registerCodes(NUMFMT_LIB, {
    NUMFMT_NON_FINITE_INPUT: {
      description: 'A formatter (formatBytes/formatDuration/formatPercent/formatNumber) received a non-finite number (NaN, +/-Infinity). The formatter returns the configured fallback (default "\u2014") and emits this warning.',
      severity: "warning",
      docsAnchor: "numeric-format/non-finite-input",
      exampleFix: "Guard the call site with `Number.isFinite(x)` before formatting, OR pass an explicit `fallback` option that matches your UX intent."
    },
    NF_SELFCHECK_INTERNAL_ERROR: {
      description: "The numeric-format `selfCheck` catch-path tripped \u2014 its try block threw unexpectedly. The lib has no rendered DOM surface so this is reserved for future opportunistic findings; an emission here means a future opportunistic check threw and was caught. Inspect the finding `message` for the underlying error.",
      severity: "info",
      docsAnchor: "numeric-format/selfcheck-internal-error",
      exampleFix: "File a bug at the numeric-format repo with the offending `message`; the catch-path should not normally fire because the current selfCheck body is a no-op."
    }
  });
} catch {
}
function warnNonFinite(fn, value, fallback) {
  try {
    warn({
      fn,
      code: "NUMFMT_NON_FINITE_INPUT",
      message: `${fn} received a non-finite value (${String(value)}); returning fallback "${fallback}".`,
      prop: "value",
      received: value,
      expected: "a finite number (Number.isFinite(value) === true)",
      dedupKey: `${fn}:non-finite`
    });
  } catch {
  }
}

// src/create-formatter.ts
var cache = /* @__PURE__ */ new Map();
function keyOf(locale, options) {
  const entries = Object.entries(options).sort(([a], [b]) => a.localeCompare(b));
  return `${locale}|${JSON.stringify(entries)}`;
}
function createFormatter(locale, options = {}) {
  const key = keyOf(locale, options);
  const hit = cache.get(key);
  if (hit) return hit;
  const fmt = new Intl.NumberFormat(locale, options);
  cache.set(key, fmt);
  return fmt;
}
function _resetFormatterCache() {
  cache.clear();
}

// src/format-bytes.ts
var KB = 1024;
var MB = 1024 * 1024;
var GB = 1024 * 1024 * 1024;
var TB = 1024 * 1024 * 1024 * 1024;
function formatBytes(value, options = {}) {
  const { precision = 1, fallback = "\u2014", locale, formatOptions } = options;
  if (!Number.isFinite(value)) {
    warnNonFinite("formatBytes", value, fallback);
    return fallback;
  }
  if (value === 0) return "0 B";
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  const formatN = (n, digits) => {
    if (locale === void 0) return n.toFixed(digits);
    try {
      return createFormatter(locale, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        ...formatOptions ?? {}
      }).format(n);
    } catch {
      return createFormatter("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        ...formatOptions ?? {}
      }).format(n);
    }
  };
  const fmt = (n, unit) => `${sign}${formatN(n, precision)} ${unit}`;
  if (abs >= TB) return fmt(abs / TB, "TB");
  if (abs >= GB) return fmt(abs / GB, "GB");
  if (abs >= MB) return fmt(abs / MB, "MB");
  if (abs >= KB) return fmt(abs / KB, "KB");
  const rounded = Math.round(abs);
  const safeSign = rounded === 0 ? "" : sign;
  return `${safeSign}${formatN(rounded, 0)} B`;
}

// src/format-duration.ts
function formatDuration(value, options = {}) {
  const { secondsPrecision = 1, fallback = "\u2014", unit = "ms" } = options;
  if (!Number.isFinite(value)) {
    warnNonFinite("formatDuration", value, fallback);
    return fallback;
  }
  if (unit === "s") {
    if (value === 0) return "0s";
    const abs2 = Math.abs(value);
    const sign2 = value < 0 ? "-" : "";
    if (abs2 < 60) return `${sign2}${Math.round(abs2)}s`;
    if (abs2 < 3600) return `${sign2}${Math.round(abs2 / 60)}m`;
    if (abs2 < 86400) return `${sign2}${Math.round(abs2 / 3600)}h`;
    return `${sign2}${Math.round(abs2 / 86400)}d`;
  }
  if (value === 0) return "0ms";
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs < 1e3) return `${sign}${Math.round(abs)}ms`;
  if (abs < 6e4) return `${sign}${(abs / 1e3).toFixed(secondsPrecision)}s`;
  if (abs < 36e5) return `${sign}${Math.round(abs / 6e4)}m`;
  if (abs < 864e5) return `${sign}${Math.round(abs / 36e5)}h`;
  return `${sign}${Math.round(abs / 864e5)}d`;
}

// src/format-percent.ts
function formatPercent(value, options = {}) {
  const { precision = 0, signed = false, fallback = "\u2014" } = options;
  if (!Number.isFinite(value)) {
    warnNonFinite("formatPercent", value, fallback);
    return fallback;
  }
  const fixed = value.toFixed(precision);
  if (!signed) return `${fixed}%`;
  if (value > 0) return `+${fixed}%`;
  return `${fixed}%`;
}

// src/format-number.ts
function formatNumber(value, options = {}) {
  const { precision = 1, alwaysCompact = false, fallback = "\u2014" } = options;
  if (!Number.isFinite(value)) {
    warnNonFinite("formatNumber", value, fallback);
    return fallback;
  }
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (!alwaysCompact && abs < 1e3) return `${sign}${Math.round(abs)}`;
  if (abs < 1e6) return `${sign}${(abs / 1e3).toFixed(precision)}k`;
  if (abs < 1e9) return `${sign}${(abs / 1e6).toFixed(precision)}M`;
  if (abs < 1e12) return `${sign}${(abs / 1e9).toFixed(precision)}B`;
  return `${sign}${(abs / 1e12).toFixed(precision)}T`;
}

// src/format-delta.ts
function formatDelta(value, options = {}) {
  const { suffix = "", precision, locale = "en-US", fallback = "\u2014" } = options;
  if (!Number.isFinite(value)) return fallback;
  const abs = Math.abs(value);
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  const fmtOpts = precision !== void 0 ? { minimumFractionDigits: precision, maximumFractionDigits: precision } : {};
  const body = new Intl.NumberFormat(locale, fmtOpts).format(abs);
  return `${sign}${body}${suffix}`;
}

// src/format-range.ts
function formatRange(low, high, options = {}) {
  const { style = "compact", locale = "en-US", precision, fallback = "\u2014" } = options;
  if (!Number.isFinite(low) || !Number.isFinite(high)) return fallback;
  const fmtOpts = precision !== void 0 ? { minimumFractionDigits: precision, maximumFractionDigits: precision } : {};
  const fmt = new Intl.NumberFormat(locale, fmtOpts);
  const a = fmt.format(low);
  const b = fmt.format(high);
  return style === "verbose" ? `${a} to ${b}` : `${a}\u2013${b}`;
}

// src/guards.ts
var isFinite = (value) => typeof value === "number" && Number.isFinite(value);
function sanitizeNumber(value, fallback = null) {
  if (typeof value !== "number") return fallback;
  if (!Number.isFinite(value)) return fallback;
  if (Object.is(value, -0)) return 0;
  return value;
}
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selfCheck(root, opts) {
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    void root;
  } catch (err) {
    findings.push({
      libId: "@norkron/web-numeric-format",
      code: "NF_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}

// src/version.ts
var LIB_VERSION = "0.3.15";
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-numeric-format",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    // Grounded — no CSS files, no JSX components, no viewport branching
    // in src/. Pure string-formatting library. Honest baseline: 'full' at
    // every tier.
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);

// src/index.ts
registerLazy(
  "@norkron/web-numeric-format",
  () => selfCheck
);

export { LIB_VERSION, RESPONSIVE_CONTRACT, _resetFormatterCache, createFormatter, describeDirection, formatBytes, formatDelta, formatDuration, formatNumber, formatPercent, formatRange, isFinite, sanitizeNumber, selfCheck };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map